export interface Point {
  x: number;
  y: number;
}

/**
 * Calculates the bounding box (top, left, width, height) of a polygon in percentage (0-100).
 */
export function getBoundingBox(points: Point[]): { top: number; left: number; width: number; height: number } {
  if (!points || points.length === 0) {
    return { top: 0, left: 0, width: 20, height: 20 };
  }

  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;

  for (const p of points) {
    if (p.x < minX) minX = p.x;
    if (p.x > maxX) maxX = p.x;
    if (p.y < minY) minY = p.y;
    if (p.y > maxY) maxY = p.y;
  }

  const left = Math.max(0, Math.min(100, minX));
  const top = Math.max(0, Math.min(100, minY));
  const width = Math.max(1, Math.min(100 - left, maxX - minX));
  const height = Math.max(1, Math.min(100 - top, maxY - minY));

  return { top, left, width, height };
}

/**
 * Calculates the centroid (geometric center) of a polygon in percentage (0-100).
 * Uses standard polygon formula, falls back to arithmetic average of vertices.
 */
export function getPolygonCentroid(points: Point[]): Point {
  if (!points || points.length === 0) return { x: 50, y: 50 };
  if (points.length === 1) return { x: points[0].x, y: points[0].y };
  if (points.length === 2) {
    return {
      x: (points[0].x + points[1].x) / 2,
      y: (points[0].y + points[1].y) / 2
    };
  }

  let signedArea = 0;
  let cx = 0;
  let cy = 0;

  for (let i = 0; i < points.length; i++) {
    const p0 = points[i];
    const p1 = points[(i + 1) % points.length];
    const a = p0.x * p1.y - p1.x * p0.y;
    signedArea += a;
    cx += (p0.x + p1.x) * a;
    cy += (p0.y + p1.y) * a;
  }

  signedArea *= 0.5;

  if (Math.abs(signedArea) < 1e-5) {
    // Degenerate polygon / colinear points fallback: arithmetic average
    let sumX = 0;
    let sumY = 0;
    for (const p of points) {
      sumX += p.x;
      sumY += p.y;
    }
    return { x: sumX / points.length, y: sumY / points.length };
  }

  cx = cx / (6 * signedArea);
  cy = cy / (6 * signedArea);

  return {
    x: Math.max(0, Math.min(100, cx)),
    y: Math.max(0, Math.min(100, cy))
  };
}

/**
 * Converts a list of Points to an SVG points string ("x1,y1 x2,y2 ...").
 */
export function pointsToSvgString(points: Point[]): string {
  if (!points || points.length === 0) return '';
  return points.map(p => `${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' ');
}

/**
 * Converts legacy rectangle properties (top, left, width, height) to a 4-point polygon.
 */
export function rectToPoints(top: number, left: number, width: number, height: number): Point[] {
  return [
    { x: left, y: top },
    { x: left + width, y: top },
    { x: left + width, y: top + height },
    { x: left, y: top + height }
  ];
}

/**
 * Parses points from points, polygon, or perimeter attributes (JSON string, array, or comma-separated string)
 * with automatic 0-1 to 0-100% coordinate normalization and rectangle fallback.
 */
export function normalizeZonePoints(
  rawPoints: any,
  fallbackRect: { top: number; left: number; width: number; height: number },
  rawPerimeter?: any
): Point[] {
  const candidate = rawPoints !== undefined && rawPoints !== null && rawPoints !== '' 
    ? rawPoints 
    : rawPerimeter;

  if (!candidate) {
    return rectToPoints(
      fallbackRect.top || 0,
      fallbackRect.left || 0,
      fallbackRect.width || 20,
      fallbackRect.height || 20
    );
  }

  let parsed: any = candidate;
  if (typeof candidate === 'string') {
    const trimmed = candidate.trim();
    if (trimmed.length > 0) {
      try {
        parsed = JSON.parse(trimmed);
      } catch {
        // SVG format fallback: "x1,y1 x2,y2 ..."
        const pairs = trimmed.split(/\s+/);
        if (pairs.length >= 3) {
          const parsedPoints: Point[] = [];
          for (const pair of pairs) {
            const [px, py] = pair.split(',').map(Number);
            if (!isNaN(px) && !isNaN(py)) {
              parsedPoints.push({ x: px, y: py });
            }
          }
          if (parsedPoints.length >= 3) return parsedPoints;
        }
      }
    }
  }

  if (Array.isArray(parsed)) {
    // Case 1: 2-point bounding box array [[minX, minY], [maxX, maxY]] (ThingsBoard 2-point format)
    if (parsed.length === 2 && Array.isArray(parsed[0]) && Array.isArray(parsed[1])) {
      let x1 = Number(parsed[0][0]) || 0;
      let y1 = Number(parsed[0][1]) || 0;
      let x2 = Number(parsed[1][0]) || 0;
      let y2 = Number(parsed[1][1]) || 0;

      // Check if coordinates are in 0..1 scale
      if (Math.max(x1, x2) <= 1.05 && Math.max(y1, y2) <= 1.05) {
        x1 *= 100;
        y1 *= 100;
        x2 *= 100;
        y2 *= 100;
      }
      return [
        { x: Math.min(x1, x2), y: Math.min(y1, y2) },
        { x: Math.max(x1, x2), y: Math.min(y1, y2) },
        { x: Math.max(x1, x2), y: Math.max(y1, y2) },
        { x: Math.min(x1, x2), y: Math.max(y1, y2) }
      ];
    }

    // Case 2: >= 3 points polygon array
    if (parsed.length >= 3) {
      let points: Point[] = [];
      for (const item of parsed) {
        if (Array.isArray(item) && item.length >= 2) {
          points.push({ x: Number(item[0]) || 0, y: Number(item[1]) || 0 });
        } else if (item && typeof item === 'object') {
          points.push({
            x: typeof item.x === 'number' ? item.x : parseFloat(item.x) || 0,
            y: typeof item.y === 'number' ? item.y : parseFloat(item.y) || 0
          });
        }
      }

      if (points.length >= 3) {
        // Auto-scale from 0..1 to 0..100 if all coordinates are <= 1.05
        const maxVal = Math.max(...points.map(p => Math.max(p.x, p.y)));
        if (maxVal > 0 && maxVal <= 1.05) {
          points = points.map(p => ({
            x: Number((p.x * 100).toFixed(2)),
            y: Number((p.y * 100).toFixed(2))
          }));
        }
        return points;
      }
    }
  }

  return rectToPoints(
    fallbackRect.top || 0,
    fallbackRect.left || 0,
    fallbackRect.width || 20,
    fallbackRect.height || 20
  );
}

/**
 * Checks if a 4-point polygon is axis-aligned (horizontal & vertical edges).
 */
export function isAxisAlignedRectangle(points: Point[], tolerance = 2.0): boolean {
  if (!points || points.length !== 4) return false;
  for (let i = 0; i < 4; i++) {
    const p1 = points[i];
    const p2 = points[(i + 1) % 4];
    const isHoriz = Math.abs(p1.y - p2.y) <= tolerance;
    const isVert = Math.abs(p1.x - p2.x) <= tolerance;
    if (!isHoriz && !isVert) return false;
  }
  return true;
}

/**
 * When dragging a corner of an axis-aligned rectangle, updates the corner and adjusts
 * adjacent vertices to maintain a perfect 90° rectangle with parallel sides.
 */
export function updateRectangleVertex(
  points: Point[],
  vertexIndex: number,
  newPos: Point
): Point[] {
  if (points.length !== 4) {
    return points.map((p, idx) => (idx === vertexIndex ? newPos : p));
  }

  const prevIdx = (vertexIndex + 3) % 4;
  const nextIdx = (vertexIndex + 1) % 4;
  const oppIdx = (vertexIndex + 2) % 4;

  const current = points[vertexIndex];
  const prev = points[prevIdx];
  const next = points[nextIdx];

  const newPoints = [...points];
  newPoints[vertexIndex] = {
    x: Math.max(0, Math.min(100, newPos.x)),
    y: Math.max(0, Math.min(100, newPos.y)),
  };

  // Determine whether prev is horizontal or vertical relative to current
  const prevIsHoriz = Math.abs(prev.y - current.y) < Math.abs(prev.x - current.x);

  if (prevIsHoriz) {
    // prev shares Y with current, next shares X with current
    newPoints[prevIdx] = { ...newPoints[prevIdx], y: newPoints[vertexIndex].y };
    newPoints[nextIdx] = { ...newPoints[nextIdx], x: newPoints[vertexIndex].x };
  } else {
    // prev shares X with current, next shares Y with current
    newPoints[prevIdx] = { ...newPoints[prevIdx], x: newPoints[vertexIndex].x };
    newPoints[nextIdx] = { ...newPoints[nextIdx], y: newPoints[vertexIndex].y };
  }

  return newPoints;
}

/**
 * Snaps a moving point to horizontal or vertical alignment with adjacent points.
 */
export function snapToOrtho(
  pos: Point,
  adjacentPoints: Point[],
  threshold = 1.5
): {
  snappedPos: Point;
  snapLines: { orientation: 'horizontal' | 'vertical'; pos: number }[];
} {
  let snappedX = pos.x;
  let snappedY = pos.y;
  const snapLines: { orientation: 'horizontal' | 'vertical'; pos: number }[] = [];

  for (const adj of adjacentPoints) {
    // Snap X if close (vertical alignment)
    if (Math.abs(pos.x - adj.x) <= threshold) {
      snappedX = adj.x;
      snapLines.push({ orientation: 'vertical', pos: adj.x });
    }
    // Snap Y if close (horizontal alignment)
    if (Math.abs(pos.y - adj.y) <= threshold) {
      snappedY = adj.y;
      snapLines.push({ orientation: 'horizontal', pos: adj.y });
    }
  }

  return {
    snappedPos: { x: snappedX, y: snappedY },
    snapLines,
  };
}

